import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import posthog from 'posthog-js';
import {
    FaUser, FaSignOutAlt, FaEnvelope, FaDatabase, FaChartBar,
    FaFire, FaBolt, FaTrophy, FaCalendarAlt,
    FaLayerGroup, FaClock, FaArrowRight, FaCog, FaLock,
    FaIdBadge, FaEdit, FaTimes, FaCamera,
} from 'react-icons/fa';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import ParticleBackground from './common/ParticleBackground';
import { sqlFundamentalsChallenges } from './exercises/config/challenges/sqlFundamentalsChallenges';
import { sqlIntermediateChallenges } from './exercises/config/challenges/sqlIntermediateChallenges';
import { paperPlusChallenges } from './exercises/config/challenges/paperPlusChallenges';
import { productMixChallenges } from './exercises/config/challenges/productMixChallenges';
import { analyticsChallenges } from './exercises/config/challenges/analyticsChallenges';
import { fintechChallenges } from './exercises/config/challenges/fintechChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_INTRO } from './exercises/config/dataVizIntroChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_INTERMEDIATE } from './exercises/config/dataVizIntermediateChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_1 } from './exercises/config/dataVizChallenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_2 } from './exercises/config/dataViz2Challenges';
import { DATA_VIZ_CHALLENGES as CHALLENGES_3 } from './exercises/config/dataViz3Challenges';

// ─── Module catalogue ─────────────────────────────────────────────────────────
const SQL_MODULES = [
    { exerciseId: 'thinking-in-data', caseId: 'sql-fundamentals', challenges: sqlFundamentalsChallenges,  label: 'SQL Fundamentals',        route: '/exercises/thinking-in-data/case/0', track: 'SQL' },
    { exerciseId: 'thinking-in-data', caseId: 'sql-intermediate', challenges: sqlIntermediateChallenges,  label: 'Filtering & Aggregation', route: '/exercises/thinking-in-data/case/1', track: 'SQL' },
    { exerciseId: 'thinking-in-data', caseId: 'paper-plus',       challenges: paperPlusChallenges,        label: 'JOINs & Relationships',   route: '/exercises/thinking-in-data/case/2', track: 'SQL' },
    { exerciseId: 'thinking-in-data', caseId: 'product-mix',      challenges: productMixChallenges,       label: 'CTEs & Subqueries',       route: '/exercises/thinking-in-data/case/3', track: 'SQL' },
    { exerciseId: 'thinking-in-data', caseId: 'analytics',        challenges: analyticsChallenges,        label: 'Window Functions',        route: '/exercises/thinking-in-data/case/4', track: 'SQL' },
    { exerciseId: 'thinking-in-data', caseId: 'fintech-payments', challenges: fintechChallenges,          label: 'Fintech Analytics',       route: '/exercises/thinking-in-data/case/5', track: 'SQL' },
];
const VIZ_MODULES = [
    { exerciseId: 'data-viz', caseId: 'data-viz-intro',        challenges: CHALLENGES_INTRO,        label: 'Foundations: Core Chart Types', route: '/exercises/data-viz-intro',        track: 'DataViz' },
    { exerciseId: 'data-viz', caseId: 'data-viz-intermediate', challenges: CHALLENGES_INTERMEDIATE, label: 'Beyond the Basics',             route: '/exercises/data-viz-intermediate', track: 'DataViz' },
    { exerciseId: 'data-viz', caseId: 'data-viz-1',            challenges: CHALLENGES_1,            label: 'Is UK Growth Slowing?',         route: '/exercises/data-viz-1',            track: 'DataViz' },
    { exerciseId: 'data-viz', caseId: 'data-viz-2',            challenges: CHALLENGES_2,            label: 'Customer Financial Behavior',   route: '/exercises/data-viz-2',            track: 'DataViz' },
    { exerciseId: 'data-viz', caseId: 'data-viz-3',            challenges: CHALLENGES_3,            label: 'Ride-Sharing Operations',       route: '/exercises/data-viz-3',            track: 'DataViz' },
];
const ALL_MODULES = [...SQL_MODULES, ...VIZ_MODULES];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const dayKey = (d) => d.toISOString().slice(0, 10);

function buildHeatmap(completedDates) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay();
    const start = new Date(today);
    start.setDate(today.getDate() - dayOfWeek - 52 * 7);

    const counts = {};
    for (const d of completedDates) {
        const k = dayKey(new Date(d));
        counts[k] = (counts[k] || 0) + 1;
    }

    const weeks = [];
    const cursor = new Date(start);
    while (cursor <= today) {
        const week = [];
        for (let d = 0; d < 7; d++) {
            const k = dayKey(cursor);
            week.push({ date: new Date(cursor), key: k, count: counts[k] || 0, isToday: k === dayKey(today), isFuture: new Date(cursor) > today });
            cursor.setDate(cursor.getDate() + 1);
        }
        weeks.push(week);
    }
    return weeks;
}

function calcStreak(completedDates) {
    if (!completedDates.length) return 0;
    const days = new Set(completedDates.map(d => dayKey(new Date(d))));
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let streak = 0;
    const cursor = new Date(today);
    if (!days.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
    while (days.has(dayKey(cursor))) { streak++; cursor.setDate(cursor.getDate() - 1); }
    return streak;
}

function cellColor(count, dark) {
    if (count === 0) return dark ? 'bg-gray-800/60 border-gray-700/40'     : 'bg-gray-100 border-gray-200/60';
    if (count === 1) return dark ? 'bg-orange-900/50 border-orange-800/40' : 'bg-orange-100 border-orange-200';
    if (count === 2) return dark ? 'bg-orange-700/60 border-orange-600/40' : 'bg-orange-300 border-orange-400/50';
    if (count === 3) return dark ? 'bg-orange-600/70 border-orange-500/50' : 'bg-orange-400 border-orange-500/50';
    return dark ? 'bg-orange-500 border-orange-400/60'                     : 'bg-orange-500 border-orange-600/50';
}

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── Component ────────────────────────────────────────────────────────────────
const Account = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [challengeRows, setChallengeRows] = useState([]);
    const isDark = document.documentElement.classList.contains('dark');

    useEffect(() => {
        if (!user) { navigate('/login'); return; }
        const load = async () => {
            setLoading(true);
            const { data: cp } = await supabase
                .from('challenge_progress')
                .select('exercise_id, case_id, challenge_id, completed_at')
                .eq('user_id', user.id)
                .eq('completed', true)
                .order('completed_at', { ascending: false });
            setChallengeRows(cp || []);
            setLoading(false);
        };
        load();
    }, [user, navigate]);

    // Build a lookup of valid challenge IDs per module key (only count current config IDs)
    const validChallengeIds = useMemo(() => {
        const map = {};
        for (const m of ALL_MODULES) {
            map[`${m.exerciseId}|${m.caseId}`] = new Set(m.challenges.map(c => c.id));
        }
        return map;
    }, []);

    // ── Derived stats - filter out stale/renamed challenge IDs ───────────────
    const filteredRows    = useMemo(() =>
        challengeRows.filter(r => {
            const key = `${r.exercise_id}|${r.case_id}`;
            return validChallengeIds[key]?.has(r.challenge_id);
        }), [challengeRows, validChallengeIds]);
    const completedDates  = useMemo(() => filteredRows.map(r => r.completed_at), [filteredRows]);
    const heatmapWeeks    = useMemo(() => buildHeatmap(completedDates), [completedDates]);
    const streak          = useMemo(() => calcStreak(completedDates), [completedDates]);
    const totalDone       = filteredRows.length;

    // challenges done per exerciseId+caseId - filtered to only current config IDs
    const moduleProgress = useMemo(() => {
        const map = {};
        for (const r of challengeRows) {
            const key = `${r.exercise_id}|${r.case_id}`;
            if (!validChallengeIds[key]?.has(r.challenge_id)) continue;
            if (!map[key]) map[key] = new Set();
            map[key].add(r.challenge_id);
        }
        return map;
    }, [challengeRows, validChallengeIds]);

    const modKey = (m) => `${m.exerciseId}|${m.caseId}`;
    const sqlCompleted = SQL_MODULES.filter(m => (moduleProgress[modKey(m)]?.size || 0) >= m.challenges.length).length;
    const vizCompleted = VIZ_MODULES.filter(m => (moduleProgress[modKey(m)]?.size || 0) >= m.challenges.length).length;

    // Last active module
    const lastExercise = useMemo(() => {
        if (!challengeRows.length) return null;
        const latest = challengeRows[0];
        const mod = ALL_MODULES.slice().reverse().find(m => m.exerciseId === latest.exercise_id && m.caseId === latest.case_id);
        return mod ? { ...mod, completedAt: latest.completed_at } : null;
    }, [challengeRows]);

    // Month labels - one label per calendar month; keep only the LAST occurrence
    // so a month that starts the grid and ends it (e.g. Feb) only shows at the end.
    const monthPositions = useMemo(() => {
        const positions = []; let lastMonth = -1;
        heatmapWeeks.forEach((week, wi) => {
            const firstDay = week.find(d => !d.isFuture);
            if (!firstDay) return;
            const m = firstDay.date.getMonth();
            if (m !== lastMonth) { positions.push({ wi, label: MONTH_LABELS[m] }); lastMonth = m; }
        });
        // Deduplicate: if the same month name appears twice (grid spans >12 months),
        // drop the earlier occurrence so the current month only shows at the end.
        const seen = new Set();
        return [...positions].reverse().filter(p => {
            if (seen.has(p.label)) return false;
            seen.add(p.label); return true;
        }).reverse();
    }, [heatmapWeeks]);

    const recentActivity = useMemo(() => filteredRows.slice(0, 8), [filteredRows]);

    // Build a lookup: 'exerciseId|caseId|challengeId' → { title, route }
    const challengeInfoMap = useMemo(() => {
        const map = {};
        for (const m of ALL_MODULES) {
            m.challenges.forEach((c, idx) => {
                // SQL: /exercises/thinking-in-data/case/N/challenge/M
                // DataViz: /exercises/data-viz-xxx?q=N (1-indexed)
                const deepRoute = m.track === 'SQL'
                    ? `${m.route}/challenge/${idx}`
                    : `${m.route}?q=${idx + 1}`;
                map[`${m.exerciseId}|${m.caseId}|${c.id}`] = { title: c.title || c.question || String(c.id), route: deepRoute, moduleLabel: m.label, track: m.track };
            });
        }
        return map;
    }, []);

    const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
    const initials    = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const memberSince = user?.created_at ? fmt(user.created_at) : '-';
    const onLogout    = async () => { await supabase.auth.signOut(); navigate('/'); };

    // ── Avatar upload ────────────────────────────────────────────────────────
    const [avatarUrl, setAvatarUrl]           = useState(user?.user_metadata?.avatar_url || null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarMsg, setAvatarMsg]           = useState(null);
    const avatarInputRef                      = useRef(null);

    // Sync if user metadata updates (e.g. after updateUser fires onAuthStateChange)
    useEffect(() => {
        if (user?.user_metadata?.avatar_url) setAvatarUrl(user.user_metadata.avatar_url);
    }, [user]);

    const uploadAvatar = useCallback(async (file) => {
        if (!file || !user) return;
        if (file.size > 2 * 1024 * 1024) { setAvatarMsg({ ok: false, text: 'Max file size is 2MB.' }); return; }
        setAvatarUploading(true); setAvatarMsg(null);
        const ext  = file.name.split('.').pop().toLowerCase();
        const path = `${user.id}/avatar.${ext}`;
        const { error: upErr } = await supabase.storage
            .from('avatars')
            .upload(path, file, { upsert: true, contentType: file.type });
        if (upErr) { setAvatarMsg({ ok: false, text: upErr.message }); setAvatarUploading(false); return; }
        const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
        const url = `${publicUrl}?t=${Date.now()}`;
        await supabase.auth.updateUser({ data: { avatar_url: url } });
        setAvatarUrl(url);
        setAvatarMsg({ ok: true, text: 'Profile photo updated!' });
        setAvatarUploading(false);
        posthog.capture('avatar_updated');
    }, [user]);

    // ── Heatmap tooltip ──────────────────────────────────────────────────────
    const [cellTooltip, setCellTooltip] = useState(null); // { label, count, x, y }

    // ── Tab state (URL-driven: ?tab=overview | ?tab=settings) ───────────────
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') === 'settings' ? 'settings' : 'overview';

    // ── Settings form state ───────────────────────────────────────────────────
    const [nameVal,       setNameVal]       = useState('');
    const [nameMsg,       setNameMsg]       = useState(null);
    const [nameSaving,    setNameSaving]    = useState(false);

    const [pwdNew,        setPwdNew]        = useState('');
    const [pwdConfirm,    setPwdConfirm]    = useState('');
    const [pwdMsg,        setPwdMsg]        = useState(null);
    const [pwdSaving,     setPwdSaving]     = useState(false);

    // Pre-fill name when user loads
    useEffect(() => {
        if (user) setNameVal(user.user_metadata?.full_name || '');
    }, [user]);

    const saveName = useCallback(async () => {
        if (!nameVal.trim()) return;
        setNameSaving(true); setNameMsg(null);
        const { error } = await supabase.auth.updateUser({ data: { full_name: nameVal.trim() } });
        setNameMsg(error ? { ok: false, text: error.message } : { ok: true, text: 'Display name updated!' });
        if (!error) posthog.capture('profile_name_updated');
        setNameSaving(false);
    }, [nameVal]);

    const savePassword = useCallback(async () => {
        if (!pwdNew || pwdNew !== pwdConfirm) {
            setPwdMsg({ ok: false, text: pwdNew ? 'Passwords do not match.' : 'Enter a new password.' });
            return;
        }
        if (pwdNew.length < 8) { setPwdMsg({ ok: false, text: 'Password must be at least 8 characters.' }); return; }
        setPwdSaving(true); setPwdMsg(null);
        const { error } = await supabase.auth.updateUser({ password: pwdNew });
        if (error) { setPwdMsg({ ok: false, text: error.message }); }
        else { setPwdMsg({ ok: true, text: 'Password updated!' }); setPwdNew(''); setPwdConfirm(''); }
        setPwdSaving(false);
    }, [pwdNew, pwdConfirm]);



    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a1220] transition-colors duration-300">
            {/* Heatmap tooltip - fixed so it escapes the overflow-x-auto container */}
            {cellTooltip && (
                <div
                    className="fixed z-[999] pointer-events-none"
                    style={{ left: cellTooltip.x, top: cellTooltip.y, transform: 'translate(-50%, -100%)' }}
                >
                    <div className="bg-gray-900 dark:bg-gray-800 text-white rounded-lg px-2.5 py-1.5 shadow-xl mb-1.5 text-center">
                        <div className="text-[11px] font-bold">
                            {cellTooltip.count === 0 ? 'No challenges' : `${cellTooltip.count} challenge${cellTooltip.count !== 1 ? 's' : ''}`}
                        </div>
                        <div className="text-[9px] text-gray-400 mt-0.5">{cellTooltip.label}</div>
                    </div>
                    {/* Arrow */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-800" />
                </div>
            )}

            {/* ── Hero banner ── */}
            <div className="relative bg-white dark:bg-[#0a1220] border-b border-gray-200 dark:border-gray-800/80 overflow-hidden pt-12 pb-16 px-4 lg:px-8">
                <ParticleBackground particleCount={40} maxDist={100} speed={0.2} />
                <div className="absolute top-0 right-1/4 w-[500px] h-[260px] bg-orange-500/[0.10] dark:bg-orange-500/[0.07] rounded-full blur-[100px] pointer-events-none" />
                <div className="absolute bottom-0 left-1/4  w-[400px] h-[180px] bg-[#1e3a5f]/[0.08]  dark:bg-[#1e3a5f]/[0.06]  rounded-full blur-[80px] pointer-events-none" />

                <div className="relative max-w-5xl mx-auto flex flex-col md:flex-row items-center md:items-end justify-between gap-8">
                    <div className="flex items-end gap-5">
                        {/* Clickable avatar with camera overlay */}
                        <div
                            className="relative flex-shrink-0 group cursor-pointer"
                            onClick={() => avatarInputRef.current?.click()}
                            title="Change profile photo"
                        >
                            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-2xl font-extrabold shadow-xl shadow-orange-500/25">
                                {avatarUrl ? (
                                    <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                                ) : (
                                    initials || <FaUser size={28} />
                                )}
                            </div>
                            <div className="absolute inset-0 rounded-2xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                {avatarUploading
                                    ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <FaCamera size={18} className="text-white" />
                                }
                            </div>
                            <input
                                ref={avatarInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={e => e.target.files?.[0] && uploadAvatar(e.target.files[0])}
                            />
                        </div>
                        <div>
                            <div className="text-[9px] font-black uppercase tracking-widest text-orange-500 mb-0.5">DataHelix Member</div>
                            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white leading-tight">{displayName}</h1>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                                <span className="flex items-center gap-1"><FaEnvelope size={9} />{user?.email}</span>
                                {avatarMsg && (
                                    <span className={`flex items-center gap-1 font-semibold ${avatarMsg.ok ? 'text-green-500' : 'text-red-500'}`}>
                                        {avatarMsg.text}
                                    </span>
                                )}
                                <span className="hidden md:inline text-gray-300 dark:text-gray-700">·</span>
                                <span className="flex items-center gap-1"><FaCalendarAlt size={9} />Joined {memberSince}</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-900/40 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all text-sm font-semibold flex-shrink-0">
                        <FaSignOutAlt size={12} /> Sign Out
                    </button>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="bg-white dark:bg-[#0a1220] border-b border-gray-200 dark:border-gray-800/80 sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 lg:px-8 flex gap-1">
                    {[{ id: 'overview', label: 'Overview', icon: FaUser }, { id: 'settings', label: 'Settings', icon: FaCog }].map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setSearchParams(id === 'overview' ? {} : { tab: id })}
                            className={`flex items-center gap-1.5 px-4 py-3.5 text-[12px] font-bold border-b-2 transition-colors ${
                                activeTab === id
                                    ? 'border-orange-500 text-orange-500'
                                    : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <Icon size={11} />{label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Main content ── */}
            <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10 space-y-6">

                {loading ? (
                    <div className="flex items-center justify-center py-32">
                        <div className="w-10 h-10 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
                    </div>
                ) : activeTab === 'settings' ? (
                    <SettingsPanel
                        user={user}
                        nameVal={nameVal} setNameVal={setNameVal} nameMsg={nameMsg} nameSaving={nameSaving} saveName={saveName}
                        pwdNew={pwdNew} setPwdNew={setPwdNew} pwdConfirm={pwdConfirm} setPwdConfirm={setPwdConfirm} pwdMsg={pwdMsg} pwdSaving={pwdSaving} savePassword={savePassword}

                    />
                ) : (
                    <>
                        {/* ── Stat cards ── */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { icon: FaBolt,       value: totalDone,           label: 'Challenges done',   color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-100 dark:border-orange-900/30' },
                                { icon: FaLayerGroup, value: sqlCompleted + vizCompleted, label: 'Modules completed', color: 'text-[#1e3a5f] dark:text-[#6b9fd4]', bg: 'bg-[#1e3a5f]/5 dark:bg-[#1e3a5f]/20', border: 'border-[#1e3a5f]/10 dark:border-[#2d5a8f]/30' },
                                { icon: FaFire,       value: streak,              label: 'Day streak',        color: 'text-red-500',    bg: 'bg-red-50 dark:bg-red-900/20',      border: 'border-red-100 dark:border-red-900/30' },
                                { icon: FaTrophy,     value: `${Math.min(100, Math.round((totalDone / 85) * 100))}%`, label: 'Platform progress', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-100 dark:border-amber-900/30' },
                            ].map(({ icon: Icon, value, label, color, bg, border }) => (
                                <div key={label} className={`${bg} ${border} border rounded-2xl p-5 flex flex-col gap-2`}>
                                    <Icon className={color} size={16} />
                                    <div className={`text-3xl font-extrabold tabular-nums ${color}`}>{value}</div>
                                    <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{label}</div>
                                </div>
                            ))}
                        </div>

                        {/* ── Activity heatmap ── */}
                        <div className="bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-sm p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="font-extrabold text-gray-900 dark:text-white text-sm flex items-center gap-2">
                                    <FaCalendarAlt className="text-orange-500" size={13} />
                                    Activity - last 52 weeks
                                </h2>
                                <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{totalDone} challenges completed</span>
                            </div>

                            <div className="overflow-x-auto">
                                <div className="flex gap-2 min-w-max">
                                    {/* Day-of-week labels column */}
                                    <div className="flex-shrink-0 w-6">
                                        {/* Spacer matches the month-label row height */}
                                        <div className="h-4 mb-1" />
                                        <div className="flex flex-col gap-[3px]">
                                            {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((lbl, i) => (
                                                <div key={i} className="h-[11px] flex items-center justify-end text-[8px] text-gray-400 dark:text-gray-600 font-medium leading-none">
                                                    {lbl}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Month labels + cell grid */}
                                    <div>
                                        {/* Month labels row - absolutely positioned */}
                                        <div className="relative h-4 mb-1">
                                            {monthPositions.map(({ wi, label }) => (
                                                <span
                                                    key={wi}
                                                    className="absolute text-[9px] text-gray-400 dark:text-gray-600 font-medium whitespace-nowrap"
                                                    style={{ left: wi * 14 }}
                                                >
                                                    {label}
                                                </span>
                                            ))}
                                        </div>
                                        {/* Cell grid */}
                                        <div className="flex gap-[3px]">
                                            {heatmapWeeks.map((week, wi) => (
                                                <div key={wi} className="flex flex-col gap-[3px]">
                                                    {week.map(cell => (
                                                        <div
                                                            key={cell.key}
                                                            className={`w-[11px] h-[11px] rounded-[2px] border transition-opacity cursor-default ${cell.isFuture ? 'opacity-0' : cellColor(cell.count, isDark)} ${cell.isToday ? 'ring-1 ring-orange-400 ring-offset-[1px] ring-offset-white dark:ring-offset-[#0a1525]' : ''}`}
                                                            onMouseEnter={cell.isFuture ? undefined : (e) => {
                                                                const r = e.currentTarget.getBoundingClientRect();
                                                                setCellTooltip({
                                                                    count: cell.count,
                                                                    label: cell.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
                                                                    x: r.left + r.width / 2,
                                                                    y: r.top - 6,
                                                                });
                                                            }}
                                                            onMouseLeave={() => setCellTooltip(null)}
                                                        />
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Legend */}
                            <div className="flex items-center justify-end gap-1.5 mt-3">
                                <span className="text-[10px] text-gray-400 dark:text-gray-600 mr-1">Less</span>
                                {[0,1,2,3,4].map(c => <div key={c} className={`w-[11px] h-[11px] rounded-[2px] border ${cellColor(c, isDark)}`} />)}
                                <span className="text-[10px] text-gray-400 dark:text-gray-600 ml-1">More</span>
                            </div>
                        </div>

                        {/* ── Two-col: track progress + activity ── */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                            {/* Track progress */}
                            <div className="bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-sm p-6 flex flex-col gap-6">
                                <h2 className="font-extrabold text-gray-900 dark:text-white text-sm flex items-center gap-2">
                                    <FaLayerGroup className="text-[#1e3a5f] dark:text-[#6b9fd4]" size={13} />
                                    Track Progress
                                </h2>

                                {/* SQL */}
                                <div className="flex flex-col gap-2.5">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-[#2d5a8f] to-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                                                <FaDatabase className="text-white" size={9} />
                                            </div>
                                            <span className="text-[10px] font-black uppercase tracking-widest text-[#1e3a5f] dark:text-[#6b9fd4]">SQL Track</span>
                                        </div>
                                        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">{sqlCompleted} of {SQL_MODULES.length} modules</span>
                                    </div>
                                    {SQL_MODULES.map((m, i) => {
                                        const done = moduleProgress[modKey(m)]?.size || 0;
                                        const isCompleted = done >= m.challenges.length;
                                        return (
                                            <Link key={i} to={m.route} className="flex items-center gap-3 group px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-[#0f1c2e]/60 transition-colors">
                                                <div className={`flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 transition-colors ${isCompleted ? 'bg-[#1e3a5f] border-[#1e3a5f]' : done > 0 ? 'border-[#4a7cbd] bg-transparent' : 'border-gray-200 dark:border-gray-700'}`} />
                                                <span className={`flex-1 text-[12px] font-medium truncate transition-colors ${done > 0 ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-600'} group-hover:text-[#1e3a5f] dark:group-hover:text-[#6b9fd4]`}>{m.label}</span>
                                                <span className={`flex-shrink-0 text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-md ${
                                                    isCompleted
                                                        ? 'bg-[#1e3a5f]/8 dark:bg-[#2d5a8f]/20 text-[#1e3a5f] dark:text-[#6b9fd4]'
                                                        : done > 0
                                                            ? 'bg-gray-100 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400'
                                                            : 'text-gray-300 dark:text-gray-700'
                                                }`}>{done}<span className="opacity-40 mx-0.5">/</span>{m.challenges.length}</span>
                                            </Link>
                                        );
                                    })}
                                </div>

                                <div className="border-t border-gray-100 dark:border-gray-800/80" />

                                {/* DataViz */}
                                <div className="flex flex-col gap-2.5">
                                    <div className="flex items-center justify-between mb-0.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-amber-600 to-amber-500 flex items-center justify-center flex-shrink-0">
                                                <FaChartBar className="text-white" size={9} />
                                            </div>
                                            <span className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-500">Data Viz Track</span>
                                        </div>
                                        <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500">{vizCompleted} of {VIZ_MODULES.length} modules</span>
                                    </div>
                                    {VIZ_MODULES.map((m, i) => {
                                        const done = moduleProgress[modKey(m)]?.size || 0;
                                        const isCompleted = done >= m.challenges.length;
                                        return (
                                            <Link key={i} to={m.route} className="flex items-center gap-3 group px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-[#0f1c2e]/60 transition-colors">
                                                <div className={`flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 transition-colors ${isCompleted ? 'bg-amber-500 border-amber-500' : done > 0 ? 'border-amber-400 bg-transparent' : 'border-gray-200 dark:border-gray-700'}`} />
                                                <span className={`flex-1 text-[12px] font-medium truncate transition-colors ${done > 0 ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-600'} group-hover:text-amber-600 dark:group-hover:text-amber-400`}>{m.label}</span>
                                                <span className={`flex-shrink-0 text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-md ${
                                                    isCompleted
                                                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                                                        : done > 0
                                                            ? 'bg-gray-100 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400'
                                                            : 'text-gray-300 dark:text-gray-700'
                                                }`}>{done}<span className="opacity-40 mx-0.5">/</span>{m.challenges.length}</span>
                                            </Link>
                                        );
                                    })}
                                </div>

                                <Link to="/exercises" className="pt-1 border-t border-gray-100 dark:border-gray-800/80 flex items-center justify-center gap-1.5 text-[11px] font-bold text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                                    Go to Practice Hub <FaArrowRight size={9} />
                                </Link>
                            </div>

                            {/* Right col */}
                            <div className="flex flex-col gap-5">

                                {/* Resume card */}
                                {lastExercise && (
                                    <div className="bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-sm p-5">
                                        <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">Continue where you left off</div>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${lastExercise.track === 'SQL' ? 'bg-gradient-to-br from-[#2d5a8f] to-[#1e3a5f] shadow-[#1e3a5f]/20' : 'bg-gradient-to-br from-orange-500 to-amber-500 shadow-orange-500/20'}`}>
                                                {lastExercise.track === 'SQL' ? <FaDatabase className="text-white" size={14} /> : <FaChartBar className="text-white" size={14} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className={`text-[9px] font-black uppercase tracking-widest mb-0.5 ${lastExercise.track === 'SQL' ? 'text-[#1e3a5f] dark:text-[#6b9fd4]' : 'text-orange-500'}`}>{lastExercise.track === 'SQL' ? 'SQL Track' : 'Data Viz Track'}</div>
                                                <div className="font-extrabold text-gray-900 dark:text-white text-sm truncate">{lastExercise.label}</div>
                                                <div className="text-[10px] text-gray-400 mt-0.5">Last activity {new Date(lastExercise.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                                            </div>
                                        </div>
                                        <Link to={lastExercise.route}>
                                            <button className={`w-full mt-4 py-2 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 text-white transition-all ${lastExercise.track === 'SQL' ? 'bg-gradient-to-r from-[#2d5a8f] to-[#1e3a5f] hover:opacity-90 shadow-md shadow-[#0f1c2e]/20' : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 shadow-md shadow-orange-900/20'}`}>
                                                Resume <FaArrowRight size={10} />
                                            </button>
                                        </Link>
                                    </div>
                                )}

                                {/* Recent activity */}
                                <div className="bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-sm p-5 flex-1">
                                    <h2 className="font-extrabold text-gray-900 dark:text-white text-sm flex items-center gap-2 mb-4">
                                        <FaClock className="text-gray-400" size={12} /> Recent activity
                                    </h2>
                                    {recentActivity.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                                            <FaBolt className="text-gray-200 dark:text-gray-700" size={28} />
                                            <p className="text-sm text-gray-400 dark:text-gray-600">No challenges completed yet.</p>
                                            <Link to="/exercises" className="text-xs font-bold text-orange-500 hover:text-orange-400">Start practising →</Link>
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5">
                                            {recentActivity.map((r, i) => {
                                                const info = challengeInfoMap[`${r.exercise_id}|${r.case_id}|${r.challenge_id}`];
                                                const isSQL = info?.track === 'SQL';
                                                const dateStr = r.completed_at ? new Date(r.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                                                const inner = (
                                                    <>
                                                        <div className={`flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center ${isSQL ? 'bg-[#1e3a5f]/5 dark:bg-[#2d5a8f]/20 text-[#1e3a5f] dark:text-[#93c5fd]' : 'bg-orange-50 dark:bg-orange-900/20 text-orange-500'}`}>
                                                            {isSQL ? <FaDatabase size={9} /> : <FaChartBar size={9} />}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[12px] font-semibold text-gray-700 dark:text-gray-300 truncate group-hover:text-orange-500 transition-colors">{info?.title || r.challenge_id}</p>
                                                            <p className="text-[10px] text-gray-400 dark:text-gray-600 truncate">{info?.moduleLabel || r.exercise_id}</p>
                                                        </div>
                                                        <span className="text-[10px] text-gray-400 dark:text-gray-600 flex-shrink-0">{dateStr}</span>
                                                    </>
                                                );
                                                return info?.route ? (
                                                    <Link key={i} to={info.route} className="group flex items-center gap-3 px-2 py-1.5 -mx-2 rounded-xl hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                                        {inner}
                                                    </Link>
                                                ) : (
                                                    <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                                                        {inner}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Streak callout ── */}
                        {streak > 0 && (
                            <div className="relative bg-gradient-to-r from-orange-500/10 to-amber-500/10 dark:from-orange-500/[0.08] dark:to-amber-500/[0.04] border border-orange-200 dark:border-orange-900/40 rounded-2xl p-6 overflow-hidden">
                                <div className="absolute right-6 top-1/2 -translate-y-1/2 text-[80px] select-none pointer-events-none opacity-10 leading-none">🔥</div>
                                <div className="flex items-center gap-4">
                                    <FaFire className="text-orange-500 flex-shrink-0" size={26} />
                                    <div>
                                        <div className="text-xl font-extrabold text-orange-500">{streak}-day streak</div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Keep it up - come back tomorrow to stay on track.</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// ─── Settings Panel ──────────────────────────────────────────────────────────
function SettingsField({ label, hint, children, msg }) {
    return (
        <div className="bg-white dark:bg-[#0a1525] rounded-2xl border border-gray-200 dark:border-gray-800/80 shadow-sm p-6">
            <div className="mb-4">
                <div className="font-extrabold text-gray-900 dark:text-white text-sm">{label}</div>
                {hint && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{hint}</p>}
            </div>
            {children}
            {msg && (
                <p className={`mt-3 text-[11px] font-semibold ${msg.ok ? 'text-emerald-500' : 'text-red-500'}`}>{msg.text}</p>
            )}
        </div>
    );
}

function SettingsPanel({
    user,
    nameVal, setNameVal, nameMsg, nameSaving, saveName,
    pwdNew, setPwdNew, pwdConfirm, setPwdConfirm, pwdMsg, pwdSaving, savePassword,
}) {
    const [editingName, setEditingName] = useState(false);
    const inputCls = 'w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#0d1b2e] text-gray-900 dark:text-white text-sm px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition';
    const btnCls  = 'flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 transition-all shadow-sm shadow-orange-900/20 disabled:opacity-50 disabled:cursor-not-allowed';

    const currentName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

    const handleSave = async () => {
        await saveName();
        setEditingName(false);
    };

    const handleCancel = () => {
        setNameVal(user?.user_metadata?.full_name || '');
        setEditingName(false);
    };

    return (
        <div className="space-y-5 max-w-2xl">
            <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-600 pb-1">Account Settings</div>

            {/* Display name */}
            <SettingsField
                label="Display name"
                hint="Shown on your profile. Updates immediately."
                msg={nameMsg}
            >
                {editingName ? (
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <FaIdBadge className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={11} />
                            <input
                                autoFocus
                                className={`${inputCls} pl-8`}
                                value={nameVal}
                                onChange={e => setNameVal(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
                                placeholder="Your display name"
                            />
                        </div>
                        <button onClick={handleSave} disabled={nameSaving || !nameVal.trim()} className={btnCls}>
                            {nameSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button onClick={handleCancel} className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                            <FaTimes size={12} />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-[#0d1b2e] border border-gray-200 dark:border-gray-700">
                            <FaIdBadge size={11} className="text-gray-400 flex-shrink-0" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{currentName}</span>
                        </div>
                        <button
                            onClick={() => setEditingName(true)}
                            className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 hover:border-orange-300 dark:hover:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition text-xs font-semibold"
                        >
                            <FaEdit size={11} /> Edit
                        </button>
                    </div>
                )}
            </SettingsField>

            {/* Password */}
            <SettingsField
                label="Change password"
                hint="Minimum 8 characters. You'll remain logged in on this device."
                msg={pwdMsg}
            >
                <div className="space-y-2.5">
                    <div className="relative">
                        <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={10} />
                        <input
                            type="password"
                            className={`${inputCls} pl-8`}
                            value={pwdNew}
                            onChange={e => setPwdNew(e.target.value)}
                            placeholder="New password"
                        />
                    </div>
                    <div className="relative">
                        <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={10} />
                        <input
                            type="password"
                            className={`${inputCls} pl-8`}
                            value={pwdConfirm}
                            onChange={e => setPwdConfirm(e.target.value)}
                            placeholder="Confirm new password"
                        />
                    </div>
                    <button onClick={savePassword} disabled={pwdSaving} className={`mt-2 ${btnCls}`}>
                        <FaLock size={10} />{pwdSaving ? 'Updating…' : 'Update password'}
                    </button>
                </div>
            </SettingsField>

        </div>
    );
}

export default Account;
